import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
} from "react";
import { toast } from "react-toastify";

const API_BASE = process.env.REACT_APP_API_URL;

// Tag Colors
const TAG_COLORS = [
  { name: "Red", hex: "#ef4444", bg: "#fee2e2", text: "#dc2626" },
  { name: "Blue", hex: "#3b82f6", bg: "#dbeafe", text: "#2563eb" },
  { name: "Green", hex: "#10b981", bg: "#d1fae5", text: "#059669" },
  { name: "Purple", hex: "#8b5cf6", bg: "#ede9fe", text: "#7c3aed" },
  { name: "Orange", hex: "#f97316", bg: "#fed7aa", text: "#ea580c" },
  { name: "Pink", hex: "#ec4899", bg: "#fce7f3", text: "#db2777" },
  { name: "Yellow", hex: "#eab308", bg: "#fef3c7", text: "#d97706" },
  { name: "Indigo", hex: "#6366f1", bg: "#e0e7ff", text: "#4f46e5" },
  { name: "Teal", hex: "#14b8a6", bg: "#ccfbf1", text: "#0d9488" },
  { name: "Gray", hex: "#6b7280", bg: "#f3f4f6", text: "#4b5563" },
];

// Tag Context
const TagContext = createContext();

// Initial State
const initialState = {
  allTags: [],
  chatTags: {},
  loading: false,
};

// Action Types
const TAG_ACTIONS = {
  SET_LOADING: "SET_LOADING",
  SET_ALL_TAGS: "SET_ALL_TAGS",
  SET_CHAT_TAGS: "SET_CHAT_TAGS",
  ADD_CHAT_TAG: "ADD_CHAT_TAG",
  REMOVE_CHAT_TAG: "REMOVE_CHAT_TAG",
  UPDATE_CHAT_TAG: "UPDATE_CHAT_TAG",
};

// Reducer
const tagReducer = (state, action) => {
  switch (action.type) {
    case TAG_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };

    case TAG_ACTIONS.SET_ALL_TAGS:
      return { ...state, allTags: action.payload };

    case TAG_ACTIONS.SET_CHAT_TAGS:
      return { ...state, chatTags: action.payload };

    case TAG_ACTIONS.ADD_CHAT_TAG:
      return {
        ...state,
        chatTags: {
          ...state.chatTags,
          [action.payload.chatId]: [
            ...(state.chatTags[action.payload.chatId] || []),
            action.payload.tag,
          ],
        },
      };

    case TAG_ACTIONS.REMOVE_CHAT_TAG:
      return {
        ...state,
        chatTags: {
          ...state.chatTags,
          [action.payload.chatId]: (
            state.chatTags[action.payload.chatId] || []
          ).filter(
            (tag) =>
              // Use composite key matching instead of id
              !(
                tag.chat_id === action.payload.chatId &&
                tag.tag === action.payload.tagName &&
                tag.instance_id === action.payload.instanceId
              )
          ),
        },
      };

    case TAG_ACTIONS.UPDATE_CHAT_TAG:
      return {
        ...state,
        chatTags: {
          ...state.chatTags,
          [action.payload.chatId]: (
            state.chatTags[action.payload.chatId] || []
          ).map((tag) =>
            // Use composite key matching for updates
            tag.chat_id === action.payload.chatId &&
            tag.tag === action.payload.oldTagName &&
            tag.instance_id === action.payload.instanceId
              ? action.payload.updatedTag
              : tag
          ),
        },
      };

    default:
      return state;
  }
};

// Tag Provider Component
export const TagProvider = ({ children, instanceId }) => {
  const [state, dispatch] = useReducer(tagReducer, initialState);

  // API Functions
  const fetchAllTags = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/user-tags/all-tags?instance_id=${instanceId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        dispatch({
          type: TAG_ACTIONS.SET_ALL_TAGS,
          payload: data.data.tags || [],
        });
      }
    } catch (error) {
      console.error("Error fetching all tags:", error);
    }
  }, [instanceId]);

  const fetchChatTags = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/user-tags/instance/${instanceId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        const tagsByChat = {};
        data.data.tags.forEach((tag) => {
          if (!tagsByChat[tag.chat_id]) {
            tagsByChat[tag.chat_id] = [];
          }
          tagsByChat[tag.chat_id].push(tag);
        });
        dispatch({ type: TAG_ACTIONS.SET_CHAT_TAGS, payload: tagsByChat });
      }
    } catch (error) {
      console.error("Error fetching chat tags:", error);
    }
  }, [instanceId]);

  const addTagToChat = useCallback(
    async (chatId, tagName, color) => {
      dispatch({ type: TAG_ACTIONS.SET_LOADING, payload: true });

      try {
        const response = await fetch(`${API_BASE}/api/user-tags/add`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
          body: JSON.stringify({
            chat_id: chatId,
            tag: tagName,
            instance_id: instanceId,
            color: color.hex,
          }),
        });

        const data = await response.json();
        if (data.success) {
          dispatch({
            type: TAG_ACTIONS.ADD_CHAT_TAG,
            payload: { chatId, tag: data.data.tag },
          });
          fetchAllTags(); // Refresh all tags
          toast.success("Tag added successfully");
          return { success: true };
        } else {
          toast.error(data.message || "Failed to add tag");
          return { success: false, error: data.message };
        }
      } catch (error) {
        console.error("Error adding tag:", error);
        toast.error("Failed to add tag");
        return { success: false, error: error.message };
      } finally {
        dispatch({ type: TAG_ACTIONS.SET_LOADING, payload: false });
      }
    },
    [instanceId, fetchAllTags]
  );

  // Updated removeTagFromChat to use composite key
  const removeTagFromChat = useCallback(
    async (chatId, tagName) => {
      try {
        // Use the new composite key endpoint
        const response = await fetch(
          `${API_BASE}/api/user-tags/remove/${instanceId}/${chatId}/${encodeURIComponent(
            tagName
          )}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "userAccessToken"
              )}`,
            },
          }
        );

        const data = await response.json();
        if (data.success) {
          dispatch({
            type: TAG_ACTIONS.REMOVE_CHAT_TAG,
            payload: { chatId, tagName, instanceId },
          });
          fetchAllTags(); // Refresh all tags
          toast.success("Tag removed successfully");
          return { success: true };
        } else {
          toast.error(data.message || "Failed to remove tag");
          return { success: false, error: data.message };
        }
      } catch (error) {
        console.error("Error removing tag:", error);
        toast.error("Failed to remove tag");
        return { success: false, error: error.message };
      }
    },
    [instanceId, fetchAllTags]
  );

  // Updated updateTag to use composite key
  const updateTag = useCallback(
    async (chatId, oldTagName, newTagName, color) => {
      try {
        const response = await fetch(
          `${API_BASE}/api/user-tags/update/${instanceId}/${chatId}/${encodeURIComponent(
            oldTagName
          )}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem(
                "userAccessToken"
              )}`,
            },
            body: JSON.stringify({
              new_tag: newTagName,
              color: color,
            }),
          }
        );

        const data = await response.json();
        if (data.success) {
          dispatch({
            type: TAG_ACTIONS.UPDATE_CHAT_TAG,
            payload: {
              chatId,
              oldTagName,
              instanceId,
              updatedTag: data.data.tag,
            },
          });
          fetchAllTags(); // Refresh all tags
          toast.success("Tag updated successfully");
          return { success: true };
        } else {
          toast.error(data.message || "Failed to update tag");
          return { success: false, error: data.message };
        }
      } catch (error) {
        console.error("Error updating tag:", error);
        toast.error("Failed to update tag");
        return { success: false, error: error.message };
      }
    },
    [instanceId, fetchAllTags]
  );

  const addExistingTagToChat = useCallback(
    async (chatId, existingTagItem) => {
      const tagName =
        typeof existingTagItem === "string"
          ? existingTagItem
          : existingTagItem.tag;
      const existingColor =
        typeof existingTagItem === "object" && existingTagItem.color
          ? existingTagItem.color
          : null;

      let tagColor = TAG_COLORS[0];
      if (existingColor) {
        const foundColor = TAG_COLORS.find((c) => c.hex === existingColor);
        if (foundColor) tagColor = foundColor;
      } else {
        // Find color from existing tags in other chats
        Object.values(state.chatTags).forEach((tags) => {
          const foundTag = tags.find((t) => t.tag === tagName);
          if (foundTag && foundTag.color) {
            const color = TAG_COLORS.find((c) => c.hex === foundTag.color);
            if (color) tagColor = color;
          }
        });
      }

      return await addTagToChat(chatId, tagName, tagColor);
    },
    [state.chatTags, addTagToChat]
  );

  const getTagColor = useCallback((tag) => {
    if (tag.color) {
      const color = TAG_COLORS.find((c) => c.hex === tag.color);
      return color || TAG_COLORS[0];
    }
    return TAG_COLORS[0];
  }, []);

  // Get available tags for filter options
  const getAvailableTagsForFilter = useCallback(() => {
    return state.allTags.map((tagItem) =>
      typeof tagItem === "string" ? tagItem : tagItem.tag
    );
  }, [state.allTags]);

  // Initialize tags on mount
  useEffect(() => {
    if (instanceId) {
      fetchAllTags();
      fetchChatTags();
    }
  }, [instanceId, fetchAllTags, fetchChatTags]);

  const value = {
    ...state,
    TAG_COLORS,
    // Actions
    fetchAllTags,
    fetchChatTags,
    addTagToChat,
    removeTagFromChat,
    updateTag,
    addExistingTagToChat,
    getTagColor,
    getAvailableTagsForFilter,
  };

  return <TagContext.Provider value={value}>{children}</TagContext.Provider>;
};

// Custom Hook
export const useTag = () => {
  const context = useContext(TagContext);
  if (!context) {
    throw new Error("useTag must be used within a TagProvider");
  }
  return context;
};
